import { Menu, MenuItem } from "@szhsin/react-menu";
import { Fragment, useLayoutEffect, useRef, useState } from "react";

import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

const GAP_PX = 8;

const tabButtonClassName = (active: boolean) =>
  classNames(
    "shrink-0 flex items-center whitespace-nowrap text-gray-900 text-sm font-medium px-2 py-1 border rounded-md h-[24px]",
    active ? "bg-gray-100" : "hover:bg-gray-50",
  );

const renderTitle = (title: ToggleButtonGroupType<string>["title"]) =>
  typeof title === "string" ? titleCase(title) : title;

const ProfileResponsiveTabs = ({
  buttons,
  onClick,
  dividerAfterIndex,
}: {
  buttons: ToggleButtonGroupType<string>[];
  onClick: (button: ToggleButtonGroupType<string>) => void;
  // Render a vertical divider after the button at this index (when visible).
  dividerAfterIndex?: number;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(buttons.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const calc = () => {
      const containerWidth = container.clientWidth;
      const children = Array.from(measure.children) as HTMLElement[];
      const buttonEls = children.slice(0, buttons.length);
      const overflowEl = children[buttons.length];
      const overflowWidth = overflowEl?.offsetWidth ?? 0;

      let totalFull = 0;
      buttonEls.forEach((el, i) => {
        totalFull += el.offsetWidth + (i > 0 ? GAP_PX : 0);
      });
      if (totalFull <= containerWidth) {
        setVisibleCount(buttons.length);
        return;
      }

      const reserved = overflowWidth + GAP_PX;
      let total = 0;
      let count = 0;
      for (let i = 0; i < buttonEls.length; i++) {
        const w = buttonEls[i].offsetWidth + (i > 0 ? GAP_PX : 0);
        if (total + w + reserved <= containerWidth) {
          total += w;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };

    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(container);
    return () => ro.disconnect();
  }, [buttons]);

  const visibleButtons = buttons.slice(0, visibleCount);
  const overflowButtons = buttons.slice(visibleCount);
  const overflowActive = overflowButtons.some((b) => b.active);
  const DotsIcon = getTopicflowIcon("horizontalDots");

  return (
    <div ref={containerRef} className="flex-1 min-w-0 relative">
      <div
        ref={measureRef}
        className="invisible pointer-events-none absolute left-0 top-0 flex"
        style={{ gap: `${GAP_PX}px` }}
        aria-hidden="true"
      >
        {buttons.map((b) => (
          <div
            key={`measure-${String(b.value)}`}
            className={tabButtonClassName(false)}
          >
            {renderTitle(b.title)}
          </div>
        ))}
        <div
          className={classNames(
            tabButtonClassName(false),
            "justify-center w-[36px] px-0",
          )}
        >
          <DotsIcon className="h-4 w-4" />
        </div>
      </div>
      <div
        className="flex items-center js-test-profile-tabs"
        style={{ gap: `${GAP_PX}px` }}
      >
        {visibleButtons.map((b, i) => (
          <Fragment key={String(b.value)}>
            <button
              type="button"
              className={tabButtonClassName(b.active)}
              disabled={b.disabled}
              onClick={() => onClick(b)}
            >
              {renderTitle(b.title)}
            </button>
            {dividerAfterIndex === i && (
              <div
                className="mx-2 h-5 w-px shrink-0 bg-gray-200"
                aria-hidden="true"
              />
            )}
          </Fragment>
        ))}
        {overflowButtons.length > 0 && (
          <Menu
            portal
            align="end"
            className="z-dropdown text-sm not-prose"
            menuButton={
              <button
                type="button"
                aria-label="More profile tabs"
                className={classNames(
                  tabButtonClassName(overflowActive),
                  "justify-center w-[36px] px-0",
                )}
              >
                <DotsIcon className="h-4 w-4" />
              </button>
            }
          >
            {overflowButtons.map((b) => (
              <MenuItem
                key={String(b.value)}
                onClick={() => onClick(b)}
                className={classNames(
                  "block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 hover:text-gray-900",
                  b.active && "bg-gray-50",
                )}
              >
                {renderTitle(b.title)}
              </MenuItem>
            ))}
          </Menu>
        )}
      </div>
    </div>
  );
};

export default ProfileResponsiveTabs;
